import { BaseRoundabout, RoundaboutApproach } from "../BaseRoundabout.js";

// const DEBUG = false;

/** Roundabout computational class. Extends the Intersection parent class */
export class Roundabout extends BaseRoundabout {
  /**
   * Constructor for the Roundabout class.  This can handle single lane roundabouts and multi-lane roundabouts.
   * @param {string} name - Name of the intersection
   * @param {Object} volumes - Object mapping direction strings (eastbound, westbound, northbound, southbound) to  {@link Volume} objects.
   * @param {Object} globalParams - Object containing the required global parameters for an intersection.
   */
  constructor(name, volumes, globalParams) {
    super(name, volumes, globalParams);

    this.EntryConfig = {
      SB: {
        slipLane: false, // false => "No", true => "Yes"
        numEntryLanes: 1, // [0,1,2]
      },
      NB: {
        slipLane: false, // false => "No", true => "Yes"
        numEntryLanes: 1, // [0,1,2]
      },
      EB: {
        slipLane: false, // false => "No", true => "Yes"
        numEntryLanes: 1, // [0,1,2]
      },
      WB: {
        slipLane: false, // false => "No", true => "Yes"
        numEntryLanes: 1, // [0,1,2]
      },
    };

    this.CircConfig = {
      SB: {
        numCircLanes: 1, // [0,1,2]
        thruLaneUtilization: 0.5, // [0 - 1.0]
      },
      NB: {
        numCircLanes: 1, // [0,1,2]
        thruLaneUtilization: 0.5, // [0 - 1.0]
      },
      EB: {
        numCircLanes: 1, // [0,1,2]
        thruLaneUtilization: 0.5, // [0 - 1.0]
      },
      WB: {
        numCircLanes: 1, // [0,1,2]
        thruLaneUtilization: 0.5, // [0 - 1.0]
      },
    };
  }

  /**
   * Function to get the DEFAULT inputs available the intersection.  This function is designed to facilitate the
   * integration of the engine into a user interface.
   *
   * A roundabout has four zones (Z1 - Z4) and is independent of Major/Minor street designation, so the four
   * cardinal directions are used.
   *
   * @return {Object} Object representation of default inputs
   */
  static getZoneDefaultInputs() {
    return {
      Z3: {
        slipLane: false, // false => "No", true => "Yes"
        numEntryLanes: 1, // [0,1,2]
        numCircLanes: 1, // [0,1,2]
        thruLaneUtilization: 0.5, // [0 - 1.0]
      },
      Z4: {
        slipLane: false, // false => "No", true => "Yes"
        numEntryLanes: 1, // [0,1,2]
        numCircLanes: 1, // [0,1,2]
        thruLaneUtilization: 0.5, // [0 - 1.0]
      },
      Z2: {
        slipLane: false, // false => "No", true => "Yes"
        numEntryLanes: 1, // [0,1,2]
        numCircLanes: 1, // [0,1,2]
        thruLaneUtilization: 0.5, // [0 - 1.0]
      },
      Z1: {
        slipLane: false, // false => "No", true => "Yes"
        numEntryLanes: 1, // [0,1,2]
        numCircLanes: 1, // [0,1,2]
        thruLaneUtilization: 0.5, // [0 - 1.0]
      },
    };
  }

  static getLaneConfigLabelsMapping() {
    return {
      Z3: this.getLabelsMappingForRoundabout(),
      Z4: this.getLabelsMappingForRoundabout(),
      Z2: this.getLabelsMappingForRoundabout(),
      Z1: this.getLabelsMappingForRoundabout(),
    };
  }

  setLaneConfigInputs(laneConfigInputs) {
    this.EntryConfig.EB.numEntryLanes = laneConfigInputs.Z3.numEntryLanes;
    this.EntryConfig.EB.slipLane = laneConfigInputs.Z3.slipLane;
    this.CircConfig.EB.numCircLanes = laneConfigInputs.Z3.numCircLanes;
    this.CircConfig.EB.thruLaneUtilization =
      laneConfigInputs.Z3.thruLaneUtilization;

    this.EntryConfig.WB.numEntryLanes = laneConfigInputs.Z4.numEntryLanes;
    this.EntryConfig.WB.slipLane = laneConfigInputs.Z4.slipLane;
    this.CircConfig.WB.numCircLanes = laneConfigInputs.Z4.numCircLanes;
    this.CircConfig.WB.thruLaneUtilization =
      laneConfigInputs.Z4.thruLaneUtilization;

    this.EntryConfig.NB.numEntryLanes = laneConfigInputs.Z2.numEntryLanes;
    this.EntryConfig.NB.slipLane = laneConfigInputs.Z2.slipLane;
    this.CircConfig.NB.numCircLanes = laneConfigInputs.Z2.numCircLanes;
    this.CircConfig.NB.thruLaneUtilization =
      laneConfigInputs.Z2.thruLaneUtilization;

    this.EntryConfig.SB.numEntryLanes = laneConfigInputs.Z1.numEntryLanes;
    this.EntryConfig.SB.slipLane = laneConfigInputs.Z1.slipLane;
    this.CircConfig.SB.numCircLanes = laneConfigInputs.Z1.numCircLanes;
    this.CircConfig.SB.thruLaneUtilization =
      laneConfigInputs.Z1.thruLaneUtilization;
  }

  /**
   * Function computing the V/C ratios and critical lane volumes for the intersection.
   */
  _runCriticalMovementAnalysis() {
    // Implement Critical Lane Volume Analysis
    let sbLeg = new RoundaboutApproach(
      "SB",
      this.SBL_MASTER,
      this.SBT_MASTER,
      this.SBR_MASTER
    );
    let nbLeg = new RoundaboutApproach(
      "NB",
      this.NBL_MASTER,
      this.NBT_MASTER,
      this.NBR_MASTER
    );
    let ebLeg = new RoundaboutApproach(
      "EB",
      this.EBL_MASTER,
      this.EBT_MASTER,
      this.EBR_MASTER
    );
    let wbLeg = new RoundaboutApproach(
      "WB",
      this.WBL_MASTER,
      this.WBT_MASTER,
      this.WBR_MASTER
    );

    let resultsSB = this._analyzeLeg(
      sbLeg,
      wbLeg,
      nbLeg,
      ebLeg,
      this.EntryConfig,
      this.CircConfig
    );
    let resultsNB = this._analyzeLeg(
      nbLeg,
      ebLeg,
      sbLeg,
      wbLeg,
      this.EntryConfig,
      this.CircConfig
    );
    let resultsEB = this._analyzeLeg(
      ebLeg,
      sbLeg,
      wbLeg,
      nbLeg,
      this.EntryConfig,
      this.CircConfig
    );
    let resultsWB = this._analyzeLeg(
      wbLeg,
      nbLeg,
      ebLeg,
      sbLeg,
      this.EntryConfig,
      this.CircConfig
    );

    // Assign results for each zone
    this._resultsByZone = {
      Z1: {
        VC: resultsSB.vcLane1,
        VC2: resultsSB.vcLane2,
      },
      Z2: {
        VC: resultsNB.vcLane1,
        VC2: resultsNB.vcLane2,
      },
      Z3: {
        VC: resultsEB.vcLane1,
        VC2: resultsEB.vcLane2,
      },
      Z4: {
        VC: resultsWB.vcLane1,
        VC2: resultsWB.vcLane2,
      },
    };
  }
}
